import type { AdminPermissionGroup, AdminRoleRecord } from '../types';

interface RolesFeatureProps {
  readonly roles: readonly AdminRoleRecord[];
  readonly permissionGroups: readonly AdminPermissionGroup[];
  readonly selectedRoleId: string;
  readonly canManage: boolean;
  readonly busyPermissionKey: string | null;
  readonly onSelectRole: (roleId: string) => void;
  readonly onTogglePermission: (roleId: string, permissionKey: string, granted: boolean) => void;
}

export function RolesFeature({
  roles,
  permissionGroups,
  selectedRoleId,
  canManage,
  busyPermissionKey,
  onSelectRole,
  onTogglePermission,
}: RolesFeatureProps) {
  const role = roles.find((item) => item.id === selectedRoleId) ?? roles[0];
  if (!role) return null;
  const protectedRole = role.name === 'Administrator';

  return (
    <section className="admin-feature" aria-labelledby="admin-roles-title">
      <div className="admin-feature__heading">
        <div>
          <span className="admin-eyebrow">Rollen & Rechte</span>
          <h2 id="admin-roles-title">Funktionen verständlich freigeben</h2>
        </div>
        <label className="admin-select-label">
          Rolle auswählen
          <select value={role.id} onChange={(event) => onSelectRole(event.target.value)}>
            {roles.map((item) => <option value={item.id} key={item.id}>{item.name}</option>)}
          </select>
        </label>
      </div>

      <div className="admin-role-summary">
        <div>
          <strong>{role.name}</strong>
          <p>{role.description}</p>
        </div>
        <span>{role.userCount} Benutzer</span>
        {protectedRole && <span className="admin-protected-badge">Geschützte Systemrolle</span>}
      </div>

      <div className="admin-permission-groups">
        {permissionGroups.map((group) => (
          <fieldset className="admin-permission-group" key={group.moduleId}>
            <legend>{group.label}</legend>
            {group.permissions.map((permission) => {
              const granted = role.permissionKeys.includes(permission.key);
              return (
                <label className="admin-permission-row" key={permission.key}>
                  <span>
                    <strong>{permission.label}</strong>
                    <small>{permission.key}</small>
                  </span>
                  <input
                    type="checkbox"
                    checked={granted}
                    disabled={!canManage || protectedRole || busyPermissionKey === permission.key}
                    onChange={(event) => onTogglePermission(role.id, permission.key, event.target.checked)}
                  />
                </label>
              );
            })}
          </fieldset>
        ))}
      </div>
      {protectedRole && <p className="admin-hint">Die Administratorrolle bleibt vollständig berechtigt. Einschränkungen erfolgen später ausschließlich über kontrollierte Benutzer-Ausnahmen.</p>}
    </section>
  );
}
